/**
 * Один экземпляр Ink на приложение.
 *
 * Через контекст, а не через модульную переменную: модульная означает,
 * что два приложения на одной странице делят сид и кэш, и настройка
 * одного меняет вид другого. Для модуля, который задуман переносимым,
 * это не теория — ровно так его и будут применять.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'

import { Ink, type InkConfig } from '../Ink'

const InkContext = createContext<Ink | null>(null)

export interface InkProviderProps extends InkConfig {
  children: ReactNode
}

export function InkProvider({ children, ...config }: InkProviderProps) {
  const ink = useMemo(() => new Ink(config), [])

  // Конфиг может меняться на живом приложении — из панели настроек,
  // например. Сид при этом трогается только если его прислали явно:
  // иначе каждая правка скорости перебрасывала бы все формы.
  useEffect(() => {
    ink.configure(config)
  }, [ink, JSON.stringify(config)])

  useEffect(() => () => ink.dispose(), [ink])

  return <InkContext.Provider value={ink}>{children}</InkContext.Provider>
}

/**
 * Доступ к экземпляру.
 *
 * Без провайдера возвращает одиночку с настройками по умолчанию: модуль
 * должен работать сразу, без обязательной обвязки. Провайдер нужен тогда,
 * когда нужны свои цвета, свой сид или своя политика выбора.
 */
let fallback: Ink | null = null

export function useInk(): Ink {
  const fromContext = useContext(InkContext)
  if (fromContext) return fromContext
  if (!fallback) fallback = new Ink()
  return fallback
}
